import Tag from '@/components/Tag.jsx';
import TextWithEllipsis from '@/components/TextWithEllipsis';
import sendToast from '@/lib/sendToast.js';
import setPath from '@/lib/setPath';
import { getWorkflowsMetadata, postWorkflowSnapshot } from '@/redux/actions/gravityActions';
import { setModalState } from '@/redux/actions/uiActions/modalActions';
import waitForSyncReady from '@/redux/actions/waitForSyncReady';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import selectors from '@/redux/selectors/selectors';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors';
import { useAnalytics } from '@/hooks/useAnalytics.js';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

const WorkflowVersionButton = () => {
  const workflowId = useArgSelector(selectors.param, 'workflowId');
  const workflowVersion = useArgSelector(selectors.param, 'workflowVersion');
  const workflowPublishedVersion = useSelector((state) => workflowScreenSelectors.activeWorkflow(state)?.publishedVersion);
  const workflowPublishedOn = useSelector((state) => workflowScreenSelectors.activeWorkflow(state)?.publishedOn);
  const workflowMetadataPublishedVersion = useSelector((state) => selectors.workflowMetadataById(state, workflowId)?.publishedVersion);
  const workflowMetadataPublishedOn = useSelector((state) => selectors.workflowMetadataById(state, workflowId)?.publishedOn);
  const changes = useSelector((state) => workflowScreenSelectors.workflowSnapshotDiffs(state)?.length) || 0;
  const { trackEvent } = useAnalytics();
  const isDraft = workflowVersion === 'draft';
  const publishedVersion = workflowPublishedVersion || workflowMetadataPublishedVersion;
  const publishedOn = workflowPublishedOn || workflowMetadataPublishedOn;
  const publishedDate = useMemo(() => {
    if (!publishedOn) return '';
    return 'Published on ' + new Date(publishedOn).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  }, [publishedOn]);
  const publishedTagText = useMemo(() => {
    if (!isDraft) {
      return publishedDate;
    }
    if (changes > 0) {
      return `${changes} Change${changes > 1 ? 's' : ''}`;
    }
    if (workflowMetadataPublishedVersion === null) {
      return 'Unpublished';
    }
    return 'No Changes';
  }, [isDraft, publishedDate, changes, workflowMetadataPublishedVersion]);

  const handleSnapshotModalConfirm = async (data) => {
    await waitForSyncReady();
    trackEvent('workflow-publishSnapshot');

    const newVersion = data?.version;
    postWorkflowSnapshot({ workflowId, snapshot: data }, () => {
      if (newVersion) {
        getWorkflowsMetadata();
        sendToast({
          duration: 3000,
          message: `Version ${newVersion} was published!`,
          action: {
            label: 'View Published',
            onClick: () => {
              setPath(`/workflows/${workflowId}/${newVersion}`);
            }
          }
        });
      }
    });
  };

  const handlePublish = () => {
    setModalState('snapshot', {
      open: true,
      mode: 'add',
      onChange: (data) => handleSnapshotModalConfirm(data)
    });
    setTimeout(() => {
      setModalState('diffs', { open: true, type: 'workflow' });
    }, 0);
  };

  return (
    <div className="workflow-version-button">
      <span className="workflow-version" data-title={!isDraft ? `Version ${publishedVersion}` : 'Draft'}>
        {!isDraft ? publishedVersion : 'Draft'}
      </span>
      {publishedTagText && (
        <Tag variant={isDraft ? 'info' : 'green'} size="sm" onClick={isDraft && changes > 0 ? handlePublish : undefined} data-title={isDraft && changes > 0 ? 'View Changes' : ''}>
          <TextWithEllipsis>{publishedTagText}</TextWithEllipsis>
        </Tag>
      )}
      {/* <Button size={'xs'} className={'primary'}>
        Edit
      </Button> */}
    </div>
  );
};

export default WorkflowVersionButton;
